import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function QuadraticCanonicalPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>Postać kanoniczna funkcji kwadratowej ma wzór:</p>
      <BlockMath math="f(x)=a(x-p)^2+q\qquad(a\ne0)" />
      <p>
        Z tej postaci od razu odczytujemy wierzchołek <InlineMath math="S=(p,q)" />
        oraz oś symetrii <InlineMath math="x=p" />. Uważamy na znak w nawiasie:
        w wyrażeniu <InlineMath math="(x-4)^2" /> mamy <InlineMath math="p=4" />,
        a w <InlineMath math="(x+4)^2" /> — <InlineMath math="p=-4" />.
      </p>
      <p>
        Znak <InlineMath math="a" /> określa kierunek ramion paraboli. Dla
        <InlineMath math="a>0" /> wierzchołek daje minimum, a dla
        <InlineMath math="a<0" /> — maksimum.
      </p>
      <p>Dla postaci ogólnej <InlineMath math="f(x)=ax^2+bx+c" /> liczymy:</p>
      <BlockMath math={String.raw`\Delta=b^2-4ac`} />
      <BlockMath math={String.raw`p=-\frac{b}{2a}\qquad q=f(p)=-\frac{\Delta}{4a}`} />
      <p>
        Parabola jest symetryczna względem prostej <InlineMath math="x=p" />. Dlatego,
        jeżeli <InlineMath math="f(x_1)=f(x_2)" /> i <InlineMath math="x_1\ne x_2" />,
        to <InlineMath math="p" /> jest średnią arytmetyczną tych argumentów:
      </p>
      <BlockMath math={String.raw`p=\frac{x_1+x_2}{2}`} />
      <p>
        Szczególnym przypadkiem są dwa miejsca zerowe, ponieważ wtedy
        <InlineMath math="f(x_1)=f(x_2)=0" />.
      </p>
      <p>
        Postać kanoniczna opisuje też przesunięcie wykresu funkcji
        <InlineMath math="y=ax^2" /> o wektor <InlineMath math={String.raw`[p,q]`} />.
        Jest to szczególny przypadek reguły <InlineMath math="g(x)=f(x-p)+q" />.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Odczytywanie a, p i q",
      description: "Zwracamy szczególną uwagę na przeciwny znak liczby p w nawiasie.",
      content: (
        <div className="space-y-3">
          <BlockMath math="f(x)=-2(x+3)^2+5" />
          <p>
            Mamy <InlineMath math="a=-2" />, <InlineMath math="p=-3" /> oraz
            <InlineMath math="q=5" />. Wierzchołkiem jest
            <InlineMath math="S=(-3,5)" />, a osią symetrii prosta
            <InlineMath math="x=-3" />. Ponieważ <InlineMath math="a<0" />,
            funkcja przyjmuje w wierzchołku największą wartość równą 5.
          </p>
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Od postaci ogólnej do kanonicznej",
      description: "Liczymy współrzędne wierzchołka i zapisujemy nową postać.",
      content: (
        <div className="space-y-3">
          <BlockMath math="f(x)=x^2-6x+5" />
          <BlockMath math={String.raw`p=-\frac{-6}{2\cdot1}=3`} />
          <BlockMath math={String.raw`q=f(3)=3^2-6\cdot3+5=-4`} />
          <BlockMath math="f(x)=(x-3)^2-4" />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Przesunięcie paraboli",
      description: "Wektor przesunięcia odczytujemy bezpośrednio z postaci kanonicznej.",
      content: (
        <div className="space-y-3">
          <p>
            Wykres <InlineMath math="f(x)=x^2" /> przesuwamy o wektor
            <InlineMath math={String.raw`[-2,3]`} />.
          </p>
          <BlockMath math={String.raw`g(x)=f(x-(-2))+3=f(x+2)+3`} />
          <BlockMath math="g(x)=(x+2)^2+3" />
          <p>Nowy wierzchołek ma współrzędne <InlineMath math="(-2,3)" />.</p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
